import { apiPath } from '../config/env';
import { httpClient } from './authenticatedHttpClient';
import { authManager } from './authManager';
import { encodeEmailForUrl } from '../utils/emailUtils';
import { DatabricksServiceApi, DatabricksCatalog, DatabricksSchema, DatabricksTableMeta, DatabricksPreviewResponse } from '../types/databricks';

// Real API implementation using unity-catalog endpoints
export const databricksServiceApi: DatabricksServiceApi = {
    async listCatalogs(): Promise<DatabricksCatalog[]> {
        const email = authManager.getUserEmail();
        if (!email) return [];
        const groups = authManager.getUserGroups();
        const endpoint = apiPath(`unity-catalog/catalogs?user_email=${encodeEmailForUrl(email)}${groups ? `&user_groups=${encodeURIComponent(groups)}` : ''}`);
        // New backend response shape includes metadata: { user, groups, catalog_count, catalogs: string[], error }
        const res = await httpClient.get<{ catalogs?: any }>(endpoint);
        if (!res.success || !res.data) return [];
        const rawCatalogs = res.data.catalogs;
        if (Array.isArray(rawCatalogs)) {
            // If catalogs is an array of strings (new shape)
            if (rawCatalogs.length && typeof rawCatalogs[0] === 'string') {
                return rawCatalogs.map((name: string) => ({ name }));
            }
            // If catalogs already an array of objects with name property (legacy / mock)
            return rawCatalogs.map((c: any) => ({ name: c.name ?? c }));
        }
        return [];
    },
    async listSchemas(catalog: string): Promise<DatabricksSchema[]> {
        const email = authManager.getUserEmail();
        if (!email) return [];
        const groups = authManager.getUserGroups();
        const endpoint = apiPath(`unity-catalog/schemas?catalog=${encodeURIComponent(catalog)}&user_email=${encodeEmailForUrl(email)}${groups ? `&user_groups=${encodeURIComponent(groups)}` : ''}`);
        const res = await httpClient.get<{ schemas?: any }>(endpoint);
        if (!res.success || !res.data) return [];
        const rawSchemas = res.data.schemas;
        if (Array.isArray(rawSchemas)) {
            // If schemas is array of strings
            if (rawSchemas.length && typeof rawSchemas[0] === 'string') {
                return rawSchemas.map((name: string) => ({ name, catalogName: catalog }));
            }
            // If schemas already objects
            return rawSchemas.map((s: any) => ({ name: s.name ?? s, catalogName: catalog }));
        }
        return [];
    },
    async listTables(catalog: string, schema: string): Promise<DatabricksTableMeta[]> {
        const email = authManager.getUserEmail();
        if (!email) return [];
        const groups = authManager.getUserGroups();
        const endpoint = apiPath(`unity-catalog/tables?catalog=${encodeURIComponent(catalog)}&schema=${encodeURIComponent(schema)}&user_email=${encodeEmailForUrl(email)}${groups ? `&user_groups=${encodeURIComponent(groups)}` : ''}`);
        const res = await httpClient.get<{ tables?: any }>(endpoint);
        if (!res.success || !res.data) return [];
        const rawTables = res.data.tables;
        if (Array.isArray(rawTables)) {
            // If tables is array of strings (names only)
            if (rawTables.length && typeof rawTables[0] === 'string') {
                return rawTables.map((name: string) => ({
                    name,
                    catalogName: catalog,
                    schemaName: schema,
                    fullName: `${catalog}.${schema}.${name}`,
                    comment: undefined,
                    columns: undefined,
                    rowCountEstimate: undefined
                }));
            }
            // If tables already array of objects with metadata
            return rawTables.map((t: any) => ({
                name: t.name ?? t,
                catalogName: catalog,
                schemaName: schema,
                fullName: `${catalog}.${schema}.${t.name ?? t}`,
                comment: t.comment,
                columns: t.columns,
                rowCountEstimate: t.row_count_estimate
            }));
        }
        return [];
    },
    async previewTable(fullName: string, limit = 5): Promise<DatabricksPreviewResponse> {
        // Add user_email for consistency if backend expects it
        const email = authManager.getUserEmail();
        const emailParam = email ? `&user_email=${encodeEmailForUrl(email)}` : '';
        const endpoint = apiPath(`databricks/preview?table_full_name=${encodeURIComponent(fullName)}&limit=${limit}${emailParam}`);
        const res = await httpClient.get<{ columns?: any[]; rows?: any[]; total_rows?: number }>(endpoint);
        return res.success ? {
            columns: (res.data?.columns || []) as any,
            rows: (res.data?.rows || []) as any,
            totalRows: res.data?.total_rows
        } : { columns: [], rows: [] };
    }
};
